import { useCallback, useEffect, useMemo, useState } from 'react'

import { Edit } from '@mui/icons-material'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Input,
  InputLabel,
  Tooltip,
} from '@mui/material'
import { AxiosError } from 'axios'
import { Button, useNotify, useTranslate } from 'react-admin'

import { Center, Divider, Progress, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import {
  useGetApiPrepaymentsIdRegenerationImpact,
  usePatchApiPrepaymentsId,
} from '../../codegen/generated/prepayments'
import { useGetApiStatementsJobStatusJobId } from '../../codegen/generated/statements'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { Prepayment } from '../../types/Prepayments'
import { LXDatePeriod } from '../LXDatePeriod'
import { PrepaymentStatementsUpdate } from './PrepaymentStatementsUpdate'

type EditPrepaymentProps = {
  prepaymentId: Prepayment['id']
  prepaymentLabel: Prepayment['label']
  prepaymentAmountInEuros: Prepayment['amountInEuros']
  prepaymentRecognitionPeriod: Prepayment['recognitionPeriod']
  onEdit: () => void
}

export const EditPrepayment = ({
  prepaymentId,
  prepaymentLabel,
  prepaymentAmountInEuros,
  prepaymentRecognitionPeriod,
  onEdit,
}: EditPrepaymentProps): JSX.Element => {
  const translate = useTranslate()
  const notify = useNotify()

  const [amountInEuros, setAmountInEuros] = useState<string>(prepaymentAmountInEuros)
  const [label, setLabel] = useState<string>(prepaymentLabel)
  const [periodMonth, setPeriodMonth] = useState(
    DateHelper.monthNumber(dayjs(prepaymentRecognitionPeriod)),
  )
  const [periodYear, setPeriodYear] = useState(
    dayjs(prepaymentRecognitionPeriod).startOf('year').get('y'),
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [jobId, setJobId] = useState<string | undefined>()

  const recognitionPeriod = useMemo(
    () => DateHelper.fromMonthYear(periodMonth, periodYear).toDate().toISOString(),
    [periodMonth, periodYear],
  )

  const prepaymentRegenerationImpactQuery = useGetApiPrepaymentsIdRegenerationImpact(
    prepaymentId,
    {
      recognitionPeriod,
      amountInEuros,
    },
    {
      query: { enabled: isModalOpen },
    },
  )
  const statementsJobStatusQuery = useGetApiStatementsJobStatusJobId(jobId ?? '', {
    query: {
      enabled: !!jobId,
      refetchInterval: data =>
        !((data?.body.job as any)?.progress === 100 && (data?.body.job as any)?.finishedOn)
          ? 2000
          : false,
    },
  })
  const prepaymentUpdateMutation = usePatchApiPrepaymentsId()

  const finaliseEditPrepayment = useCallback(() => {
    notify(
      translate('pages.prepayments.editPrepaymentModal.success', {
        label: prepaymentLabel,
      }),
      { type: 'success' },
    )

    onEdit()
    setIsLoading(false)
    setIsModalOpen(false)
    setJobId(undefined)
  }, [notify, onEdit, prepaymentLabel, translate])

  const onEditPrepayment = useCallback(async () => {
    setIsLoading(true)
    try {
      if (typeof label !== 'undefined' && typeof amountInEuros !== 'undefined') {
        const res = await prepaymentUpdateMutation.mutateAsync({
          id: prepaymentId,
          data: {
            label,
            amountInEuros,
            ...(recognitionPeriod ? { recognitionPeriod } : undefined),
          },
        })

        if (res.body.jobId) {
          setJobId(res.body.jobId)
        } else {
          finaliseEditPrepayment()
        }
      }
    } catch (e: unknown) {
      const message =
        (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e

      notify(`${translate('pages.prepayments.editPrepaymentModal.error')} - ${message}`, {
        type: 'error',
      })
      console.error('Error - edit prepayment', e)
    }
  }, [
    prepaymentUpdateMutation.mutateAsync,
    prepaymentId,
    translate,
    label,
    amountInEuros,
    recognitionPeriod,
    finaliseEditPrepayment,
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch if year or month has changed
  useEffect(() => {
    if (isModalOpen) {
      prepaymentRegenerationImpactQuery.refetch()
    }
  }, [isModalOpen, periodYear, periodMonth, prepaymentRegenerationImpactQuery.refetch])

  useEffect(() => {
    if (
      (statementsJobStatusQuery.data?.body.job as any)?.progress === 100 &&
      (statementsJobStatusQuery.data?.body.job as any)?.finishedOn
    ) {
      finaliseEditPrepayment()
    }
  }, [statementsJobStatusQuery.data?.body.job, finaliseEditPrepayment])

  return (
    <>
      <Tooltip title={translate('_generics.edit')} placement="top" arrow>
        <span>
          <Button
            size="small"
            color="error"
            onClick={() => setIsModalOpen(true)}
            style={{
              minWidth: 'fit-content',
            }}
          >
            <Edit
              color="secondary"
              fontSize="small"
              style={{
                marginLeft: '0.5rem',
              }}
            />
          </Button>
        </span>
      </Tooltip>
      <Dialog
        aria-labelledby="prepayments-edit"
        open={isModalOpen}
        // maxWidth="sm"
        fullWidth
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle id="prepayments-edit">
          {translate('pages.prepayments.editPrepaymentModal.title', {
            label: prepaymentLabel,
          })}
        </DialogTitle>
        <DialogContent dividers>
          {typeof jobId !== 'string' ? (
            <>
              <Grid container direction="row" spacing={4}>
                <Grid item>
                  <FormControl>
                    <InputLabel htmlFor="label">{translate('_generics.label')}</InputLabel>
                    <Input
                      id="label"
                      name="label"
                      defaultValue={prepaymentLabel}
                      aria-describedby="label"
                      value={label}
                      onChange={e => setLabel(e.currentTarget.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item>
                  <FormControl>
                    <InputLabel htmlFor="amountInEuros">
                      {translate('pages.prepayments.amountInEuros')}
                    </InputLabel>
                    <Input
                      id="amountInEuros"
                      name="amountInEuros"
                      type="number"
                      defaultValue={prepaymentAmountInEuros}
                      inputProps={{
                        min: 0,
                        step: 0.1,
                      }}
                      aria-describedby="amountInEuros"
                      value={amountInEuros}
                      onChange={e => setAmountInEuros(e.currentTarget.value)}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container item lg={6} direction="row" spacing={4} mt={0.05}>
                <Grid item pt={0.1}>
                  <LXDatePeriod
                    month={periodMonth?.toString()}
                    year={periodYear?.toString()}
                    monthLabel={translate('_generics.period')}
                    yearLabel=" "
                    onUpdate={(month: string | undefined, year: string | undefined): void => {
                      if (month) {
                        setPeriodMonth(parseInt(month))
                      }
                      if (year) {
                        setPeriodYear(parseInt(year))
                      }
                    }}
                  />
                </Grid>
              </Grid>
              {(prepaymentRegenerationImpactQuery.data?.body.statements ?? []).length > 0 && (
                <Divider my="xl" w="100%" />
              )}
              <PrepaymentStatementsUpdate
                statements={prepaymentRegenerationImpactQuery.data?.body.statements ?? []}
                loading={prepaymentRegenerationImpactQuery.isLoading}
              />
            </>
          ) : (
            <Stack gap="xs">
              <Progress value={75} striped animated />
              <Center>
                <Text size="sm" fs="italic">
                  {translate('pages.prepayments.xStatementsBeingRegen', {
                    count: prepaymentRegenerationImpactQuery.data?.body.statements.length ?? 0,
                  })}
                </Text>
              </Center>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false)
            }}
            label={translate('_generics.cancel')}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onEditPrepayment}
            label={translate('_generics.confirm')}
            disabled={
              isLoading || typeof label === 'undefined' || typeof amountInEuros === 'undefined'
            }
          >
            <Edit />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
